/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { AwvInfo, TasksApi, JobsApi } from "client";
import { useMapDataContext } from "context/MapDataProvider";
import { useUserContext } from "context/UserContext";
import { useApiWithTimeout } from "hooks/ApiWrapperWithTimeout";
import { useCallApi } from "hooks/CallApiHook";
import { Const } from "const";
import moment, { Moment } from "moment";
import { useAwvDataContext } from "context/AwvDataProvider";
import { useEffect, useRef } from "react";

export const useDayJob = (date: Moment, targetAwv?: AwvInfo) => {
  const user = useUserContext();
  const { jobsite } = useUserContext()
  const { mapData } = useMapDataContext();
  const { awvData } = useAwvDataContext();
  const [tasksApi, cancelTasksApi] = useApiWithTimeout(
    TasksApi,
    Const.operationTimeout.getTask
  );
  const [jobsApi] = useApiWithTimeout(
    JobsApi,
    Const.operationTimeout.getJob
  );

  const awvRef = useRef(awvData.awvInfos);

  useEffect(() => {
    awvRef.current = awvData.awvInfos;
  }, [awvData.awvInfos]);

  return useCallApi(
    async () => {
      const resultAwvs = targetAwv ? [targetAwv] : awvRef.current;
      const frameTypeAndNumbers = resultAwvs
        ?.map((elm) => elm.frameTypeAndNumber)
        .filter(
          (elm): elm is Exclude<typeof elm, undefined> => elm !== undefined
        );
      if (
        frameTypeAndNumbers === undefined ||
        frameTypeAndNumbers.length === 0
      ) {
        return { awvInfos: undefined, taskInfos: undefined };
      }
      const tasks = mapData.mapId
        ? await jobsApi.getJob({
            frameTypeAndNumberList: frameTypeAndNumbers,
            mapId: mapData.mapId,
            date: moment.utc(date.format("YYYY-MM-DD")).toDate(),
            timezone: user.jobsite?.timezone,
            jobSiteId: jobsite?.jobSiteId,
          })
        : undefined;
      return { awvInfos: resultAwvs, taskInfos: tasks?.taskInfos ?? [] };
    },
    cancelTasksApi,
    [
      user.jobsite?.timezone,
      mapData.mapId,
      mapData.mapVer,
      date,
      targetAwv,
      tasksApi,
      cancelTasksApi,
    ]
  );
};
